import type { User } from '@/@types/user'
import { css } from '@emotion/react'
import { MoreIcon } from '@ruguoapp/jike-fe-icons'
import { map } from 'lodash-es'
import { type HTMLAttributes, useMemo } from 'react'
import Avatar from './Avatar'
import ClickWrapper from './ClickWrapper'

export interface ClickableStackAvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
	data: User[]
	isMoreIconShow?: boolean
}

function UserAvatar({ user, index }: { user: User; index: number }) {
	const wrapperStyle = useMemo(
		() => css`
			position: absolute;
			z-index: 1;
			top: 0;
			left: ${index * 42 - (index === 0 ? 0 : index * 9)}px;
			width: 42px;
			height: 42px;
		`,
		[index]
	)

	return (
		<ClickWrapper key={user.id} css={wrapperStyle}>
			<Avatar
				data={user}
				css={css`
					.jk-avatar {
						transition: outline-color 0.2s;
						outline: 2px solid transparent;

						&:hover {
							outline: 2px solid var(--tint-shadow-1);
						}
					}
				`}
			/>
		</ClickWrapper>
	)
}

function MoreButton({ userCount }: { userCount: number }) {
	const wrapperStyle = useMemo(
		() => css`
			position: absolute;
			z-index: 1;
			top: 0;
			left: ${3 * 40 - 3 * 9}px;
			width: 40px;
			height: 40px;
		`,
		[]
	)

	return (
		<ClickWrapper
			onClick={(e) => {
				e.stopPropagation()
			}}
			css={wrapperStyle}
		>
			<div
				css={css`
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					background-color: var(--tint-tertiary);
					border: 1px solid var(--tint-pic-border);
					color: var(--solid-white-1);
					border-radius: 50%;
					transition: outline-color 0.2s;
					outline: 2px solid transparent;

					&:hover {
						outline: 2px solid var(--tint-shadow-1);
					}
				`}
			>
				<MoreIcon
					css={css`
						width: 24px;
						height: 24px;
						user-select: none;
						-webkit-user-drag: none;
					`}
				/>
			</div>
		</ClickWrapper>
	)
}

export default function ClickableStackAvatarGroup({
	data: users,
	isMoreIconShow,
	...props
}: ClickableStackAvatarGroupProps) {
	const containerStyle = useMemo(
		() => css`
			position: relative;
			width: ${users.length * 42 - (users.length - 1) * 9}px;
			height: 42px;
		`,
		[users.length]
	)

	return (
		<div css={containerStyle} {...props}>
			{map(users, (user, index) => (
				<UserAvatar key={user.id} user={user} index={index} />
			))}

			{isMoreIconShow && <MoreButton userCount={users.length} />}
		</div>
	)
}
